import { useState, useEffect, Link, useNavigate, axios, Button, Card, CardContent, CardHeader, CardTitle, CardDescription, Badge, Loader2, Users, FileText, Target, TrendingUp, LogOut, Check, LOGO_URL, API, useAuth, toast } from "@/pages/shared";

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [content, setContent] = useState([]);
  const [leads, setLeads] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [usage, setUsage] = useState(null);
  const [siteSettings, setSiteSettings] = useState({ show_landing_stats: false });
  const [health, setHealth] = useState(null);
  const [healthBusy, setHealthBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedContent, setSelectedContent] = useState([]);
  const [deletingContent, setDeletingContent] = useState(false);
  const [contentTotal, setContentTotal] = useState(0);
  const [loadingMoreContent, setLoadingMoreContent] = useState(false);
  const navigate = useNavigate();
  const { token } = useAuth();

  const toggleContentSelect = (id) =>
    setSelectedContent((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const loadMoreContent = async () => {
    setLoadingMoreContent(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.get(`${API}/admin/content?skip=${content.length}&limit=50`, { withCredentials: true, headers });
      setContent((prev) => [...prev, ...res.data.content]);
      setContentTotal(res.data.total);
    } catch (e) { toast.error("Failed to load more content"); }
    setLoadingMoreContent(false);
  };

  const deleteSelectedContent = async () => {
    if (!selectedContent.length) return;
    if (!window.confirm(`Permanently delete ${selectedContent.length} content item(s)? This cannot be undone.`)) return;
    setDeletingContent(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.post(`${API}/admin/content/delete`, { ids: selectedContent }, { withCredentials: true, headers });
      toast.success(`Deleted ${res.data.deleted} item(s)`);
      setContent((prev) => prev.filter((c) => !selectedContent.includes(c.id)));
      setContentTotal((prev) => Math.max(0, prev - res.data.deleted));
      setStats((prev) => (prev ? { ...prev, total_content: Math.max(0, prev.total_content - res.data.deleted) } : prev));
      setSelectedContent([]);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    setDeletingContent(false);
  };

  useEffect(() => { checkAdmin(); }, []);

  const checkAdmin = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.get(`${API}/admin/verify`, { withCredentials: true, headers });
      setIsAdmin(true);
      fetchData();
    } catch (e) { navigate("/admin"); }
  };

  const fetchData = async () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const config = { withCredentials: true, headers };
    try {
      const [statsRes, usersRes, contentRes, leadsRes, revenueRes, analyticsRes, settingsRes, engagementRes, usageRes, healthRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, config),
        axios.get(`${API}/admin/users`, config),
        axios.get(`${API}/admin/content`, config),
        axios.get(`${API}/admin/leads`, config),
        axios.get(`${API}/admin/revenue`, config),
        axios.get(`${API}/admin/analytics`, config),
        axios.get(`${API}/admin/settings`, config),
        axios.get(`${API}/admin/email-engagement?limit=50`, config).catch(() => ({ data: null })),
        axios.get(`${API}/admin/usage?limit=20&days=30`, config).catch(() => ({ data: null })),
        axios.get(`${API}/admin/system-health`, config).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data); setUsers(usersRes.data.users); setContent(contentRes.data.content);
      setContentTotal(contentRes.data.total || contentRes.data.content.length);
      setLeads(leadsRes.data.leads); setRevenue(revenueRes.data); setAnalytics(analyticsRes.data); setSiteSettings(settingsRes.data);
      setEngagement(engagementRes.data);
      setUsage(usageRes.data);
      setHealth(healthRes.data);
    } catch (e) { toast.error("Failed to load admin data"); }
    setLoading(false);
  };

  const updateUser = async (userId, updates) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try { await axios.patch(`${API}/admin/users/${userId}`, updates, { withCredentials: true, headers }); toast.success("User updated"); fetchData(); }
    catch (e) { toast.error("Failed to update user"); }
  };

  const handleLogout = async () => { await axios.post(`${API}/admin/logout`, {}, { withCredentials: true }); navigate("/admin"); };

  const recheckHealth = async (service) => {
    setHealthBusy(service);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.post(`${API}/admin/system-health/recheck/${service}`, {}, { withCredentials: true, headers });
      const fresh = await axios.get(`${API}/admin/system-health`, { withCredentials: true, headers });
      setHealth(fresh.data);
      toast.success(`Re-checked ${service}`);
    } catch (e) {
      toast.error(`Re-check failed for ${service}`);
    }
    setHealthBusy(null);
  };

  const toggleSetting = async (key, value) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try { const res = await axios.put(`${API}/admin/settings`, { [key]: value }, { withCredentials: true, headers }); setSiteSettings(res.data.settings); toast.success(`Setting updated`); }
    catch (e) { toast.error("Failed to update setting"); }
  };

  if (!isAdmin) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <nav className="fixed top-0 w-full z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Creatara - Admin Dashboard for Content Platform Management" className="w-10 h-10 rounded-lg" />
            <span className="text-xl font-bold">Admin Panel</span>
            <Badge className="bg-violet-500/20 text-violet-500">Admin</Badge>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/"><Button variant="ghost" size="sm">View Site</Button></Link>
            <Button variant="outline" size="sm" onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
          </div>
        </div>
      </nav>

      <div className="pt-20 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {["overview", "users", "content", "leads", "revenue", "growth", "engagement", "usage", "health", "settings"].map((tab) => (
              <Button key={tab} variant={activeTab === tab ? "default" : "ghost"} onClick={() => setActiveTab(tab)} className={`rounded-full capitalize ${activeTab === tab ? "bg-violet-600" : ""}`}>{tab}</Button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>
          ) : (
            <>
              {activeTab === "overview" && stats && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { icon: Users, value: stats.total_users, label: "Total Users", color: "blue" },
                      { icon: FileText, value: stats.total_content, label: "Content Created", color: "teal" },
                      { icon: Target, value: stats.total_leads, label: "Leads Captured", color: "amber" },
                      { icon: TrendingUp, value: stats.total_affiliate_clicks, label: "Affiliate Clicks", color: "purple" }
                    ].map((item, i) => (
                      <Card key={i} className="glass">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl bg-${item.color}-500/20 flex items-center justify-center`}><item.icon className={`w-6 h-6 text-${item.color}-500`} /></div>
                            <div><p className="text-2xl font-bold">{item.value}</p><p className="text-sm text-muted-foreground">{item.label}</p></div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="glass">
                      <CardHeader><CardTitle>Users by Plan</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {Object.entries(stats.users_by_plan).map(([plan, count]) => (
                            <div key={plan} className="flex items-center justify-between">
                              <span className="capitalize">{plan}</span>
                              <div className="flex items-center gap-4">
                                <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${plan === "free" ? "bg-gray-500" : plan === "pro" ? "bg-blue-500" : "bg-purple-500"}`} style={{width: `${(count / stats.total_users) * 100}%`}}></div>
                                </div>
                                <span className="font-medium w-12 text-right">{count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="glass">
                      <CardHeader><CardTitle>Today's Activity</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 rounded-lg bg-white/5"><span>New Users</span><Badge className="bg-teal-500/20 text-teal-500">{stats.users_today}</Badge></div>
                          <div className="flex items-center justify-between p-4 rounded-lg bg-white/5"><span>Content Generated</span><Badge className="bg-blue-500/20 text-blue-500">{stats.content_today}</Badge></div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              {activeTab === "users" && (
                <Card className="glass">
                  <CardHeader><CardTitle>All Users ({users.length})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead><tr className="border-b border-white/10"><th className="text-left p-3 text-sm font-medium">User</th><th className="text-left p-3 text-sm font-medium">Plan</th><th className="text-left p-3 text-sm font-medium">Content</th><th className="text-left p-3 text-sm font-medium">Status</th><th className="text-left p-3 text-sm font-medium">Joined</th><th className="text-left p-3 text-sm font-medium">Actions</th></tr></thead>
                        <tbody>
                          {users.map((u) => (
                            <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                              <td className="p-3"><p className="font-medium">{u.name}</p><p className="text-sm text-muted-foreground">{u.email}</p></td>
                              <td className="p-3"><select value={u.plan} onChange={(e) => updateUser(u.id, { plan: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1 text-sm"><option value="free">Free</option><option value="pro">Pro</option><option value="enterprise">Enterprise</option></select></td>
                              <td className="p-3">{u.content_count || 0}</td>
                              <td className="p-3"><Badge className={u.is_active !== false ? "bg-teal-500/20 text-teal-500" : "bg-violet-500/20 text-violet-500"}>{u.is_active !== false ? "Active" : "Inactive"}</Badge></td>
                              <td className="p-3 text-sm text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                              <td className="p-3"><Button size="sm" variant="ghost" onClick={() => updateUser(u.id, { is_active: u.is_active === false })}>{u.is_active !== false ? "Deactivate" : "Activate"}</Button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {activeTab === "content" && (
                <Card className="glass">
                  <CardHeader>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <CardTitle data-testid="admin-content-count-title">Generated Content — showing {content.length} of {contentTotal}</CardTitle>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="border-white/15 text-xs" onClick={() => setSelectedContent(selectedContent.length === content.length ? [] : content.map((c) => c.id))} data-testid="admin-content-select-all-btn">
                          {selectedContent.length === content.length && content.length > 0 ? "Deselect all" : `Select all loaded (${content.length})`}
                        </Button>
                        <Button size="sm" className="bg-red-600 hover:bg-red-700 text-xs" disabled={!selectedContent.length || deletingContent} onClick={deleteSelectedContent} data-testid="admin-content-delete-selected-btn">
                          {deletingContent ? <Loader2 className="w-3 h-3 animate-spin" /> : `Delete selected (${selectedContent.length})`}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {content.map((c) => (
                        <div key={c.id} className={`p-4 rounded-lg border transition-colors ${selectedContent.includes(c.id) ? "bg-red-500/10 border-red-500/40" : "bg-white/5 border-white/10"}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <input type="checkbox" checked={selectedContent.includes(c.id)} onChange={() => toggleContentSelect(c.id)} className="w-4 h-4 accent-red-500 cursor-pointer" data-testid={`admin-content-checkbox-${c.id}`} />
                              <Badge variant="secondary">{c.content_type}</Badge>
                              {c.status && <Badge className="bg-white/10 text-muted-foreground border-white/15 text-[10px]">{c.status}</Badge>}
                              <span className="font-medium">{c.topic}</span>
                            </div>
                            <span className="text-sm text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{c.generated_content}...</p>
                          <p className="text-xs text-muted-foreground">By: {c.user_name} ({c.user_email})</p>
                        </div>
                      ))}
                    </div>
                    {content.length < contentTotal && (
                      <div className="text-center mt-4">
                        <Button size="sm" variant="outline" className="border-white/15" onClick={loadMoreContent} disabled={loadingMoreContent} data-testid="admin-content-load-more-btn">
                          {loadingMoreContent ? <Loader2 className="w-4 h-4 animate-spin" /> : `Load more (${contentTotal - content.length} remaining)`}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {activeTab === "leads" && (
                <Card className="glass">
                  <CardHeader><CardTitle>Captured Leads ({leads.length})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead><tr className="border-b border-white/10"><th className="text-left p-3 text-sm font-medium">Data</th><th className="text-left p-3 text-sm font-medium">Form</th><th className="text-left p-3 text-sm font-medium">Date</th></tr></thead>
                        <tbody>
                          {leads.map((l, i) => (
                            <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                              <td className="p-3"><pre className="text-sm">{JSON.stringify(l.data, null, 2)}</pre></td>
                              <td className="p-3">{l.form_id}</td>
                              <td className="p-3 text-sm text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {activeTab === "revenue" && revenue && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="glass"><CardContent className="p-6 text-center"><p className="text-4xl font-bold text-teal-500">£{revenue.total_revenue}</p><p className="text-muted-foreground">Total Revenue</p></CardContent></Card>
                    <Card className="glass"><CardContent className="p-6 text-center"><p className="text-4xl font-bold text-blue-500">£{revenue.by_plan.pro}</p><p className="text-muted-foreground">Pro Plans</p></CardContent></Card>
                    <Card className="glass"><CardContent className="p-6 text-center"><p className="text-4xl font-bold text-purple-500">£{revenue.by_plan.enterprise}</p><p className="text-muted-foreground">Enterprise Plans</p></CardContent></Card>
                  </div>
                  <Card className="glass"><CardContent className="p-6 text-center"><p className="text-2xl font-bold">{revenue.total_transactions}</p><p className="text-muted-foreground">Total Transactions</p></CardContent></Card>
                </div>
              )}

              {activeTab === "growth" && analytics && (
                <div className="space-y-6" data-testid="admin-growth-tab">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Recurring Revenue</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-teal-500">£{analytics.revenue.mrr_gbp}</p><p className="text-sm text-muted-foreground">MRR</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-teal-400">£{analytics.revenue.arr_gbp}</p><p className="text-sm text-muted-foreground">ARR (projected)</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-blue-500">{analytics.revenue.active_pro}</p><p className="text-sm text-muted-foreground">Active Pro</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-purple-500">{analytics.revenue.active_enterprise}</p><p className="text-sm text-muted-foreground">Active Enterprise</p></CardContent></Card>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3">
                      <Card className="glass"><CardContent className="p-4"><p className="text-xl font-bold text-amber-500">{analytics.revenue.past_due}</p><p className="text-xs text-muted-foreground">Past due</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-4"><p className="text-xl font-bold text-violet-500">{analytics.revenue.cancelled}</p><p className="text-xs text-muted-foreground">Cancelled</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-4"><p className="text-xl font-bold text-violet-400">{analytics.revenue.refunded}</p><p className="text-xs text-muted-foreground">Refunded</p></CardContent></Card>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Growth (last 30 days)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold">{analytics.growth.new_users_30d}</p><p className="text-sm text-muted-foreground">New users (30d)</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold">{analytics.growth.new_users_7d}</p><p className="text-sm text-muted-foreground">New users (7d)</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-teal-500">{analytics.growth.free_to_paid_conversion_pct}%</p><p className="text-sm text-muted-foreground">Free → Paid</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-violet-500">{analytics.growth.monthly_churn_pct}%</p><p className="text-sm text-muted-foreground">Churn rate</p></CardContent></Card>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Viral / Referral Loop</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold">{analytics.viral.referred_signups}</p><p className="text-sm text-muted-foreground">Referred signups</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold">{analytics.viral.referred_paid_conversions}</p><p className="text-sm text-muted-foreground">Referred paid</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-teal-500">{analytics.viral.referral_conversion_pct}%</p><p className="text-sm text-muted-foreground">Referral → Paid</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-amber-500">{analytics.viral.k_factor}</p><p className="text-sm text-muted-foreground">K-factor</p></CardContent></Card>
                    </div>
                    {analytics.viral.top_referrers.length > 0 && (
                      <Card className="glass mt-3">
                        <CardHeader><CardTitle className="text-base">Top 5 Referrers</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                          {analytics.viral.top_referrers.map((r, i) => (
                            <div key={i} className="flex items-center justify-between p-2 rounded bg-white/5">
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{r.name || "—"}</p>
                                <p className="text-xs text-muted-foreground truncate">{r.email}</p>
                              </div>
                              <Badge>{r.referral_credits} credits</Badge>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Engagement</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold">{analytics.engagement.total_content}</p><p className="text-sm text-muted-foreground">Total content</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold">{analytics.engagement.content_last_30d}</p><p className="text-sm text-muted-foreground">Content (30d)</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold">{analytics.engagement.total_videos}</p><p className="text-sm text-muted-foreground">Total videos</p></CardContent></Card>
                      <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold">{analytics.engagement.videos_last_30d}</p><p className="text-sm text-muted-foreground">Videos (30d)</p></CardContent></Card>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "usage" && (
                <div className="space-y-6" data-testid="usage-panel">
                  {!usage ? (
                    <Card className="glass"><CardContent className="p-6 text-sm text-muted-foreground">No usage data yet. Once users start generating content, the LLM cost breakdown will appear here.</CardContent></Card>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <Card className="glass"><CardContent className="p-4"><p className="text-2xl font-bold text-teal-400">{usage.global.text_calls}</p><p className="text-xs text-muted-foreground">Text gens · ${usage.cost_assumptions.text}/call</p></CardContent></Card>
                        <Card className="glass"><CardContent className="p-4"><p className="text-2xl font-bold text-blue-400">{usage.global.image_calls}</p><p className="text-xs text-muted-foreground">Image gens · ${usage.cost_assumptions.image}/call</p></CardContent></Card>
                        <Card className="glass"><CardContent className="p-4"><p className="text-2xl font-bold text-amber-400">{usage.global.video_calls}</p><p className="text-xs text-muted-foreground">Sora 2 · ${usage.cost_assumptions.video}/call</p></CardContent></Card>
                        <Card className="glass"><CardContent className="p-4"><p className="text-2xl font-bold text-violet-400">${usage.global.estimated_cost_usd}</p><p className="text-xs text-muted-foreground">{usage.window_days}-day burn</p></CardContent></Card>
                        <Card className="glass"><CardContent className="p-4"><p className="text-2xl font-bold">${usage.global.estimated_cost_per_day}</p><p className="text-xs text-muted-foreground">Per-day avg · ~${(usage.global.estimated_cost_per_day * 30).toFixed(0)}/mo</p></CardContent></Card>
                      </div>
                      <Card className="glass">
                        <CardHeader>
                          <CardTitle>Top spenders — last {usage.window_days} days</CardTitle>
                          <CardDescription>Watch for free-plan users with high burn (potential abuse) or paid users approaching unit-economic break-even.</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {usage.by_user_recent.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-4 text-center">No generation activity yet in this window.</p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm" data-testid="usage-table">
                                <thead>
                                  <tr className="text-left text-xs text-muted-foreground border-b border-white/10">
                                    <th className="py-2 pr-3">User</th>
                                    <th className="py-2 pr-3">Plan</th>
                                    <th className="py-2 pr-3">Text</th>
                                    <th className="py-2 pr-3">Image</th>
                                    <th className="py-2 pr-3">Video</th>
                                    <th className="py-2 pr-3">Est. $ burn</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {usage.by_user_recent.map((u) => {
                                    const flag = u.plan === "free" && u.estimated_cost_usd > 1;
                                    return (
                                      <tr key={u.id} className={`border-b border-white/5 ${flag ? "bg-violet-500/5" : ""}`} data-testid={`usage-row-${u.id}`}>
                                        <td className="py-2 pr-3">
                                          <div className="font-medium">{u.name || "—"}{flag && <Badge className="ml-2 bg-violet-500/20 text-violet-400 text-[10px]">over budget</Badge>}</div>
                                          <div className="text-xs text-muted-foreground">{u.email}</div>
                                        </td>
                                        <td className="py-2 pr-3"><Badge variant="secondary" className="capitalize">{u.plan}</Badge></td>
                                        <td className="py-2 pr-3">{u.text}</td>
                                        <td className="py-2 pr-3">{u.image}</td>
                                        <td className="py-2 pr-3">{u.video}</td>
                                        <td className="py-2 pr-3 font-mono font-bold text-violet-400">${u.estimated_cost_usd}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      <Card className="glass">
                        <CardHeader>
                          <CardTitle>Protections active</CardTitle>
                          <CardDescription>How the app is protecting your Universal Key wallet.</CardDescription>
                        </CardHeader>
                        <CardContent className="text-sm space-y-2">
                          <div>✅ <strong>Plan-tier monthly caps</strong>: text 10/100/∞ · image 2/30/200 · video 0/5/50</div>
                          <div>✅ <strong>Hourly burst limit</strong>: {usage.hourly_burst_limit} generations/user/hour across all types</div>
                          <div>✅ <strong>Free users blocked from video</strong>: Sora 2 requires Pro+ (£29/mo)</div>
                          <div>✅ <strong>Referral abuse</strong>: dedup + 25 invites/user/day</div>
                          <div>⚠️ <strong>Not yet:</strong> disposable-email-domain blocklist on signup</div>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>
              )}

              {activeTab === "engagement" && (
                <div className="space-y-6" data-testid="engagement-panel">
                  {!engagement ? (
                    <Card className="glass">
                      <CardContent className="p-6 text-sm text-muted-foreground">
                        Email engagement events haven't been received yet. Configure the Resend webhook for{" "}
                        <code className="px-1 bg-white/10 rounded">/api/webhooks/resend</code> in your Resend dashboard
                        and set <code className="px-1 bg-white/10 rounded">RESEND_WEBHOOK_SECRET</code> in the backend
                        environment.
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-teal-400">{engagement.totals.sent}</p><p className="text-sm text-muted-foreground">Emails sent</p></CardContent></Card>
                        <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-blue-400">{engagement.totals.opened}</p><p className="text-sm text-muted-foreground">Opens · {Math.round((engagement.totals.open_rate || 0) * 100)}%</p></CardContent></Card>
                        <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-amber-400">{engagement.totals.clicked}</p><p className="text-sm text-muted-foreground">Clicks · {Math.round((engagement.totals.click_rate || 0) * 100)}%</p></CardContent></Card>
                        <Card className="glass"><CardContent className="p-6"><p className="text-3xl font-bold text-violet-400">{engagement.totals.bounced}</p><p className="text-sm text-muted-foreground">Bounced</p></CardContent></Card>
                      </div>
                      <Card className="glass">
                        <CardHeader>
                          <CardTitle>Hottest leads — sorted by engagement</CardTitle>
                          <CardDescription>Score = opens × 1 + clicks × 3. Target the top of this list for sales follow-up after each drip step.</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {engagement.users.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-6 text-center">No engagement events yet.</p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm" data-testid="engagement-table">
                                <thead>
                                  <tr className="text-left text-xs text-muted-foreground border-b border-white/10">
                                    <th className="py-2 pr-3">User</th>
                                    <th className="py-2 pr-3">Plan</th>
                                    <th className="py-2 pr-3">Score</th>
                                    <th className="py-2 pr-3">Opens</th>
                                    <th className="py-2 pr-3">Clicks</th>
                                    <th className="py-2 pr-3">Drips sent</th>
                                    <th className="py-2 pr-3">Last activity</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {engagement.users.map((u) => (
                                    <tr key={u.id} className="border-b border-white/5" data-testid={`engagement-row-${u.id}`}>
                                      <td className="py-2 pr-3">
                                        <div className="font-medium">{u.name || "—"}</div>
                                        <div className="text-xs text-muted-foreground">{u.email}{u.email_unreachable && <Badge className="ml-2 bg-violet-500/20 text-violet-400 text-[10px]">unreachable</Badge>}</div>
                                      </td>
                                      <td className="py-2 pr-3"><Badge variant="secondary" className="capitalize">{u.plan}</Badge></td>
                                      <td className="py-2 pr-3 font-mono font-bold text-amber-400">{u.engagement_score}</td>
                                      <td className="py-2 pr-3">{u.opens}</td>
                                      <td className="py-2 pr-3">{u.clicks}</td>
                                      <td className="py-2 pr-3 text-xs">
                                        {["day2","day7","day14"].filter((d) => u.drip_sent?.[d]).join(", ") || "—"}
                                      </td>
                                      <td className="py-2 pr-3 text-xs text-muted-foreground">{u.last_event_at ? new Date(u.last_event_at).toLocaleString() : "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>
              )}

              {activeTab === "health" && (
                <div className="space-y-4" data-testid="admin-system-health">
                  <Card className="glass">
                    <CardHeader>
                      <CardTitle>Integration health</CardTitle>
                      <CardDescription>
                        Live state of every background health probe. Daily/weekly cron jobs update these; admins get a Resend email the moment any go red.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {!health && <p className="text-sm text-muted-foreground">Health data unavailable.</p>}
                      {health?.services?.map((svc) => {
                        const ok = svc.healthy === true;
                        const unknown = svc.healthy === null || svc.healthy === undefined;
                        const dotColor = ok ? "bg-teal-500" : unknown ? "bg-zinc-400" : "bg-violet-500";
                        const badgeText = ok ? "Healthy" : unknown ? "Not yet probed" : "BROKEN";
                        const badgeClass = ok ? "bg-teal-500/20 text-teal-300 border-teal-500/40"
                          : unknown ? "bg-zinc-500/20 text-zinc-300 border-zinc-500/40"
                          : "bg-violet-500/20 text-violet-300 border-violet-500/40";
                        return (
                          <div
                            key={svc.key}
                            data-testid={`health-row-${svc.key}`}
                            className="p-4 rounded-lg bg-white/5 border border-white/10 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-1">
                                <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotColor}`} />
                                <p className="font-medium">{svc.label}</p>
                                <Badge variant="outline" className={badgeClass}>{badgeText}</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mb-2">{svc.purpose}</p>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                <span>Cadence: {svc.cadence}</span>
                                {svc.last_checked_at && (
                                  <span>Last check: {new Date(svc.last_checked_at).toLocaleString()}</span>
                                )}
                                {svc.last_status != null && <span>Status: {svc.last_status}</span>}
                                {svc.extra && (
                                  <span>{svc.extra.total_connections} connections · {svc.extra.revoked} revoked</span>
                                )}
                              </div>
                              {!ok && !unknown && svc.last_error_body && (
                                <pre className="mt-2 p-2 rounded bg-black/40 text-[11px] text-violet-200 overflow-x-auto max-h-24">{svc.last_error_body}</pre>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`health-recheck-${svc.key}`}
                              disabled={healthBusy === svc.key}
                              onClick={() => recheckHealth(svc.key)}
                            >
                              {healthBusy === svc.key ? <Loader2 className="w-4 h-4 animate-spin" /> : "Re-check"}
                            </Button>
                          </div>
                        );
                      })}
                      {health?.checked_at && (
                        <p className="text-[11px] text-muted-foreground text-right">Snapshot at {new Date(health.checked_at).toLocaleString()}</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {activeTab === "settings" && (
                <div className="space-y-6">
                  <Card className="glass">
                    <CardHeader>
                      <CardTitle>Landing Page Settings</CardTitle>
                      <CardDescription>Control what visitors see on the public landing page</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10" data-testid="stats-toggle-row">
                        <div>
                          <p className="font-medium">Show Stats Counter</p>
                          <p className="text-sm text-muted-foreground">Display "Active Users", "Content Generated", "Leads Captured", and "Affiliate Clicks" on the landing page</p>
                        </div>
                        <button
                          onClick={() => toggleSetting("show_landing_stats", !siteSettings.show_landing_stats)}
                          className={`relative w-12 h-6 rounded-full transition-colors ${siteSettings.show_landing_stats ? "bg-violet-600" : "bg-white/20"}`}
                          data-testid="stats-toggle-switch"
                        >
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${siteSettings.show_landing_stats ? "translate-x-6" : ""}`} />
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {siteSettings.show_landing_stats ? "Stats counter is currently visible on the landing page." : "Stats counter is currently hidden from the landing page."}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
